import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Upload, Download } from "lucide-react";
import BulkUploadModal from "@/components/BulkUploadModal";
import RemarksCell from "@/components/RemarksCell";
import { useAuth } from "@/context/AuthContext";
import ScoreCardCell, { scoreColumns, SCORE_LABELS } from "@/components/ScoreCardCell";

export default function Tenders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [cards, setCards] = useState({});
  const [q, setQ] = useState("");
  // Single filter strip (fix_duplicate_tenders_and_filters §2.2): Open · Closed · Submitted.
  // Replaces 3 overlapping filter rows. Default = Open.
  const [statusFilter, setStatusFilter] = useState("open"); // open | closed | submitted
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [newIds, setNewIds] = useState(new Set()); // session-flagged new rows

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, sc] = await Promise.all([api.get("/tenders"), api.get("/tenders/score-cards").catch(() => ({ data: {} }))]);
      setItems(data);
      setCards(sc.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const todayIso = new Date().toISOString().slice(0, 10);
  const isOpenByDate = (t) => (t.deadline || "") >= todayIso;
  // Filter semantics (§2.3 + §4.3):
  //   open      → closing date in the future AND not Submitted/Won/Lost (still actively biddable)
  //   closed    → closing date has passed (includes Won/Lost — they're concluded past tenders)
  //   submitted → state === SUBMITTED (user has explicitly marked as submitted)
  const matchesStatusFilter = (t) => {
    const state = (t.state || "OPEN").toUpperCase();
    if (statusFilter === "submitted") return state === "SUBMITTED";
    if (statusFilter === "closed")    return !isOpenByDate(t) || state === "WON" || state === "LOST";
    /* open */                         return isOpenByDate(t) && state !== "SUBMITTED" && state !== "WON" && state !== "LOST";
  };

  const scoreCols = scoreColumns(user, statusFilter);
  const colCount = (isAdmin ? 11 : 10) + scoreCols.length;
  const filtered = items.filter((t) =>
    matchesStatusFilter(t) &&
    (q === "" || (`${t.title} ${t.client} ${t.location || ""} ${t.tender_type || ""} ${t.gem_type || ""} ${t.remarks || ""} ${isAdmin ? (t.created_by_name || "") : ""}`).toLowerCase().includes(q.toLowerCase()))
  );

  const counts = useMemo(() => {
    let open = 0, closed = 0, submitted = 0;
    items.forEach((t) => {
      const state = (t.state || "OPEN").toUpperCase();
      if (state === "SUBMITTED") submitted++;
      else if (state === "WON" || state === "LOST" || !isOpenByDate(t)) closed++;
      else open++;
    });
    return { open, closed, submitted };
  }, [items, todayIso]);

  const onTenderUpdated = (updated) => {
    setItems((arr) => arr.map((t) => (t.id === updated.id ? updated : t)));
  };

  const onImported = (ids) => {
    setNewIds(new Set(ids));
    load();
  };

  const downloadOne = async (t) => {
    try {
      const resp = await api.get(`/tenders/${t.id}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      // Pull suggested filename from Content-Disposition
      const cd = resp.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const fname = match?.[1] || `Tender_${t.sr_no || "NA"}.csv`;
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="tenders-page">
      <div className="flex items-end justify-between border-b border-ink-line pb-6 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Pipeline · Master Sheet</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Tender List</h1>
          <p className="text-sm text-ink-muted mt-2">Track every tender through its 5-stage lifecycle.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowUpload(true)}
            data-testid="tenders-upload-btn"
            className="border border-ink-line text-ink hover:bg-secondary px-4 py-2.5 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors"
          >
            <Upload className="w-4 h-4" /> Upload Tender Sheet
          </button>
          <Link
            to="/tenders/new"
            data-testid="tenders-new-btn"
            className="bg-brand hover:bg-brand-hover text-white px-4 py-2.5 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Tender
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="tenders-search"
            placeholder={isAdmin ? "Search by title, client, location, type, remarks, creator…" : "Search by title, client, location, type, remarks…"}
            className="w-full pl-9 pr-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
          />
        </div>
        {/* Single filter strip — Open / Closed / Submitted (fix_duplicate_tenders_and_filters §2.2) */}
        <div className="flex gap-0 border border-ink-line rounded-sm bg-white overflow-hidden" data-testid="status-filter-strip">
          {[
            { k: "open",      label: "Open",      count: counts.open },
            { k: "closed",    label: "Closed",    count: counts.closed },
            { k: "submitted", label: "Submitted", count: counts.submitted },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setStatusFilter(o.k)}
              data-testid={`status-filter-${o.k}`}
              className={`px-4 py-2 text-[12px] uppercase tracking-wider border-r border-ink-line last:border-r-0 transition-colors ${
                statusFilter === o.k ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"
              }`}
            >
              {o.label}<span className="font-num ml-1.5">{o.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[1300px]">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-3 py-3 font-medium">#</th>
              <th className="px-3 py-3 font-medium">Tender No.</th>
              <th className="px-3 py-3 font-medium">Tender Title</th>
              <th className="px-3 py-3 font-medium">Client</th>
              {isAdmin && <th className="px-3 py-3 font-medium" data-testid="created-by-header">Created By</th>}
              <th className="px-3 py-3 font-medium">Location</th>
              <th className="px-3 py-3 font-medium">Closing Date</th>
              <th className="px-3 py-3 font-medium">Category</th>
              <th className="px-3 py-3 font-medium">Budget</th>
              <th className="px-3 py-3 font-medium">Notes</th>
              {scoreCols.map((k) => (
                <th key={k} className="px-3 py-3 font-medium whitespace-nowrap" data-testid={`score-header-${k}`}>{SCORE_LABELS[k]}</th>
              ))}
              <th className="px-3 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={colCount} className="px-5 py-10 text-center text-ink-muted uppercase tracking-wider text-xs">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={colCount} className="px-5 py-10 text-center text-ink-muted">
                No tenders. <Link to="/tenders/new" className="text-brand underline">Add one</Link> or <button onClick={() => setShowUpload(true)} className="text-brand underline">upload a sheet</button>.</td></tr>
            )}
            {filtered.map((t) => (
              <tr key={t.id} className={`border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors ${newIds.has(t.id) ? "bg-good-soft/40" : ""}`} data-testid={`tender-row-${t.id}`}>
                <td className="px-3 py-2.5 font-num text-ink-muted">
                  {newIds.has(t.id) && <span className="chip border-good/40 bg-good-soft text-good text-[9px] mr-1 px-1 py-0">NEW</span>}
                  {t.sr_no || "—"}
                </td>
                <td className="px-3 py-2.5 font-num text-ink-muted text-xs">{t.tender_no || "—"}</td>
                <td className="px-3 py-2.5 font-medium max-w-[260px]">
                  <Link to={`/tenders/${t.id}`} className="hover:text-brand truncate block" title={t.title} data-testid={`tender-link-${t.id}`}>{t.title}</Link>
                </td>
                <td className="px-3 py-2.5">{t.client}</td>
                {isAdmin && (
                  <td className="px-3 py-2.5 text-ink-muted text-[12px]" data-testid={`tender-created-by-${t.id}`}>
                    {t.created_by_name || "—"}
                  </td>
                )}
                <td className="px-3 py-2.5 text-ink-muted">{t.location || "—"}</td>
                <td className="px-3 py-2.5 font-num">{t.deadline || "—"}</td>
                <td className="px-3 py-2.5 text-ink-muted">{t.tender_type || "—"}</td>
                <td className="px-3 py-2.5 font-num">{t.contract_value || "—"}</td>
                <td className="px-3 py-2.5 max-w-[200px]"><RemarksCell tender={t} onUpdated={onTenderUpdated} /></td>
                {scoreCols.map((k) => (
                  <td key={k} className="px-3 py-2.5"><ScoreCardCell tender={t} kind={k} card={cards[t.id]?.[k]} user={user} /></td>
                ))}
                <td className="px-3 py-2.5 text-right">
                  <button
                    onClick={() => downloadOne(t)}
                    title="Download tender details"
                    data-testid={`download-tender-${t.id}`}
                    className="p-1.5 text-ink-muted hover:text-brand hover:bg-secondary rounded-sm transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <BulkUploadModal onClose={() => setShowUpload(false)} onImported={onImported} />
      )}
    </div>
  );
}
